import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import type { AgentSuggestion } from '../../api/agent';

type SuggestionOrigin = 'live' | 'restored';

interface SuggestionState {
  /** Everything the turn proposed, before the settled filter. */
  all: AgentSuggestion[];
  /** Ids the user has dismissed or accepted; hidden but not forgotten. */
  settled: Set<string>;
  origin: SuggestionOrigin;
  /** undefined until the user touches the toggle, so origin decides the default. */
  userCollapsed: boolean | undefined;

  /** Derived: `all` minus `settled`. See `derive` for why it is stored. */
  suggestions: AgentSuggestion[];
  /** Derived: `userCollapsed`, or folded by default when restored from history. */
  collapsed: boolean;
}

const INITIAL: SuggestionState = {
  all: [],
  settled: new Set(),
  origin: 'live',
  userCollapsed: undefined,
  suggestions: [],
  collapsed: false,
};

export function suggestionId(suggestion: AgentSuggestion): string {
  return (
    suggestion.fingerprint ??
    `${suggestion.key}:${JSON.stringify(suggestion.params ?? {})}:${suggestion.title}`
  );
}

export function isDurable(suggestion: AgentSuggestion): boolean {
  return typeof suggestion.fingerprint === 'string' && suggestion.fingerprint.length > 0;
}

/* Recomputes the derived fields, reusing the previous array when the members are unchanged */
function derive(state: SuggestionState): SuggestionState {
  const next = state.all.filter((suggestion) => !state.settled.has(suggestionId(suggestion)));
  const collapsed = state.userCollapsed ?? state.origin === 'restored';

  const sameMembers =
    next.length === state.suggestions.length &&
    next.every((item, index) => item === state.suggestions[index]);

  return {
    ...state,
    suggestions: sameMembers ? state.suggestions : next,
    collapsed,
  };
}

const useStore = create<SuggestionState>(() => INITIAL);

/** Applies a change to the raw inputs, then refreshes what is derived from them. */
function update(change: (state: SuggestionState) => Partial<SuggestionState>): void {
  useStore.setState((state) => derive({ ...state, ...change(state) }), true);
}

/** Whether two published sets propose the same things, in the same order. */
function sameSuggestions(a: AgentSuggestion[], b: AgentSuggestion[]): boolean {
  return a.length === b.length && a.every((item, i) => suggestionId(item) === suggestionId(b[i]));
}

export function publishSuggestions(
  next: AgentSuggestion[],
  options?: { origin?: SuggestionOrigin; settled?: string[] },
): void {
  update((state) => {
    const changed = !sameSuggestions(next, state.all);

    return {
      all: changed ? next : state.all,
      origin: options?.origin ?? 'live',
      userCollapsed: changed ? undefined : state.userCollapsed,
      settled: options?.settled ? new Set(options.settled) : state.settled,
    };
  });
}

export function dismissSuggestion(suggestion: AgentSuggestion): void {
  update((state) => ({ settled: new Set(state.settled).add(suggestionId(suggestion)) }));
}

export function setSuggestionsCollapsed(collapsed: boolean): void {
  update(() => ({ userCollapsed: collapsed }));
}

export function clearSuggestions(): void {
  publishSuggestions([]);
}

export function resetSuggestionStore(): void {
  useStore.setState({ ...INITIAL, settled: new Set(), suggestions: [] }, true);
}

interface SuggestionSnapshot {
  suggestions: AgentSuggestion[];
  collapsed: boolean;
}

/* `useShallow` so the returned object is itself stable */
export function useAgentSuggestions(): SuggestionSnapshot {
  return useStore(useShallow(({ suggestions, collapsed }) => ({ suggestions, collapsed })));
}

export function useDismissSuggestion(): (suggestion: AgentSuggestion) => void {
  return dismissSuggestion;
}

export function suggestionState(): SuggestionSnapshot {
  const { suggestions, collapsed } = useStore.getState();
  return { suggestions, collapsed };
}
